import type { AuthResponse, ProblemDetails } from '../generated/schema';

export interface ApiClientConfig {
  baseUrl: string;
  getToken?: () => Promise<string | null> | string | null;
  getRefreshToken?: () => Promise<string | null> | string | null;
  onToken?: (token: string | null) => Promise<void> | void;
  onRefreshToken?: (token: string | null) => Promise<void> | void;
  getHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
  onUnauthorized?: () => Promise<void> | void;
  fetch?: typeof globalThis.fetch;
}

let config: ApiClientConfig = {
  baseUrl: typeof process !== 'undefined' && process.env?.EXPO_PUBLIC_API_URL
    ? process.env.EXPO_PUBLIC_API_URL
    : '',
};
let memoryToken: string | null = null;
let refreshPromise: Promise<string | null> | null = null;

export function setApiConfig(next: Partial<ApiClientConfig>) {
  config = { ...config, ...next };
}

export function getApiConfig() {
  return config;
}

export function setAccessToken(token: string | null) {
  memoryToken = token;
  return config.onToken?.(token);
}

export async function getAccessToken() {
  return (await config.getToken?.()) ?? memoryToken;
}

export class ApiError extends Error {
  constructor(public readonly status: number, public readonly problem: ProblemDetails) {
    super(problem.detail || problem.title);
    this.name = 'ApiError';
  }
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  retryAuth?: boolean;
}

function endpoint(path: string) {
  if (/^https?:\/\//.test(path)) return path;
  const base = config.baseUrl.replace(/\/$/, '');
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalized}`;
}

export async function request<T = any>(path: string, options: RequestOptions = {}): Promise<T> {
  const fetcher = config.fetch ?? globalThis.fetch;
  if (!fetcher) throw new Error('No fetch implementation is available');
  const token = await getAccessToken();
  const customHeaders = await config.getHeaders?.();
  const headers = new Headers(options.headers);
  for (const [name, value] of Object.entries(customHeaders ?? {})) headers.set(name, value);
  if (token) headers.set('Authorization', `Bearer ${token}`);

  let body: BodyInit | undefined;
  if (options.body instanceof FormData) {
    body = options.body;
  } else if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    body = JSON.stringify(options.body);
  }

  const response = await fetcher(endpoint(path), {
    ...options,
    body,
    headers,
    credentials: 'include',
  });

  if (response.status === 401 && options.retryAuth !== false && !path.endsWith('/auth/refresh')) {
    const next = await refreshAccessToken();
    if (next) return request<T>(path, { ...options, retryAuth: false });
    await config.onUnauthorized?.();
  }

  if (!response.ok) {
    let problem: ProblemDetails;
    try {
      problem = await response.json() as ProblemDetails;
    } catch {
      problem = { type: 'about:blank', title: response.statusText, status: response.status, detail: response.statusText };
    }
    throw new ApiError(response.status, problem);
  }
  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

export async function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = Promise.resolve(config.getRefreshToken?.()).then(refreshToken =>
      request<AuthResponse>('/v1/auth/refresh', { method: 'POST', body: refreshToken ? { refreshToken } : undefined, retryAuth: false }),
    )
      .then(async response => {
        await setAccessToken(response.accessToken);
        if (response.refreshToken) await config.onRefreshToken?.(response.refreshToken);
        return response.accessToken;
      })
      .catch(async () => {
        await setAccessToken(null);
        await config.onRefreshToken?.(null);
        return null;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

export const apiClient = {
  get: <T = any>(url: string, options: any = {}) => request<T>(url, options),
  post: <T = any>(url: string, body?: unknown, options: any = {}) => request<T>(url, { ...options, method: 'POST', body }),
  patch: <T = any>(url: string, body?: unknown, options: any = {}) => request<T>(url, { ...options, method: 'PATCH', body }),
  put: <T = any>(url: string, body?: unknown, options: any = {}) => request<T>(url, { ...options, method: 'PUT', body }),
  delete: <T = any>(url: string, options: any = {}) => request<T>(url, { ...options, method: 'DELETE' }),
};
